import * as React from "react";
import { Callout, getTheme, mergeStyles, Stack, Text, ISearchBoxStyles, Icon, Persona, PersonaSize, IStyle, FontWeights, DirectionalHint, ITextStyles, DialogType, Dialog, DialogFooter, PrimaryButton } from "@fluentui/react";
import { SearchBox } from "./SearchBox/SearchBox";
import { LanguageContext } from "../../../../utilities/LocalizationModule";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../redux";
import { setSearch, setSearchFilterType } from "../../redux/modules/search";
import { getFileTypeIconProps } from "@fluentui/react-file-type-icons";
import { FileIconType } from "@fluentui/react-file-type-icons";
import { setContentManagerSearchFilterType, setContentManagerSearchValue } from "../../redux/modules/contentManagerDirectory";
import { IGraphGetUsersRequest } from "../_v2/redux/api/graph/api-graph-interfaces";
import authModule from "../../../../utilities/AuthModule";
import { logger } from "../../../../utilities/logger";
import { useLazyGetAllUsersQuery, useLazyGetUsersQuery } from "../_v2/redux/api/graph/api-graph-slice";
import { ComponentTypes } from "../../config/config";
import generalModule from "../../../../utilities/GeneralModule";
import { FilterTypeEnum } from "../enums/FilterTypeEnum";
import { validateEmail } from "../helpers/validateEmail";
import { CacheHelper } from "../../../../utilities/CacheHelper";
import { advancedSearch, DocubirdCache, shared } from "../../config/constants";
import { EmailCreatorFactory } from "../helpers/emailAttachment/EmailCreatorFactory";
import { EmailCreationTypeEnum } from "../enums/EmailCreationEnum";
import { IAdvancedSearchContext } from "../../interfaces/IAdvancedSearch";
import { setMode } from "../../redux/modules/mode";
import { ModeEnum } from "../enums/modeEnum";
import { AdvancedSearchHelper } from "../helpers/advancedSearchHelper";
import { isMobile } from "../../styles/mobileStyles";

const theme = getTheme();

const dropDownSearchStyles: Record<string, React.CSSProperties> | IStyle = {
  stackMain: {
    width: '100%',
  },
  callout: {
    marginTop: -11,
    maxHeight: '400px',
    // minHeight: '220px',
    minWidth: '90vw',
  },
  dropdownElementContainerDiv: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '220px'
  },
  calloutContainer: {
    backgroundColor: 'blue',
  },
  calloutRoot: {
    backgroundColor: 'green',
  },
  dropdownElement: {
    marginTop: 15,
    marginBottom: 15,
  },
  dropdownSection: {
    marginLeft: 10,
    marginTop: 10,
  },
  dropdownSectionItems: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    marginTop: 5
  },
  historySearchItem: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    marginRight: 10,
    cursor: 'pointer',
  },
  iconSpacing: {
    marginLeft: 7,
    marginRight: 7,
  },
  quickSearchPadding: {
    padding: '10px 5px',
  },
  svgIcon: {
    fonzSize: 16
  }
};

const filterButtonStyles = {
  filterButton: mergeStyles({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '4px 10px',
    borderRadius: 18,
    margin: '5px 2px 0 2px',
    border: "1px solid #c5c5c5"
  }),
  filterButtonIcon: mergeStyles({
    marginRight: 3,
    height: '16px',
  }),
};

const localSearchBoxStyles: Partial<ISearchBoxStyles> = {
  root: {
    borderColor: theme.palette.themeDarkAlt,
    position: "relative",
    zIndex: 9998
  },
  iconContainer: {
    alignItems: "center"
  }
};

const headingTextStyles: ITextStyles = { root: { fontSize: 16, fontWeight: FontWeights.semibold, color: "#605e5c" } }

const searchingForArray = [
  { id: 0, buttonText: 'Word', extension: '.docx', filterType: FilterTypeEnum.Word },
  { id: 1, buttonText: 'PowerPoint', extension: '.ppt', filterType: FilterTypeEnum.PowerPoint },
  { id: 2, buttonText: 'Excel', extension: '.xls', filterType: FilterTypeEnum.Excel },
  { id: 3, buttonText: 'PDF', extension: '.pdf', filterType: FilterTypeEnum.PDF },
  { id: 4, buttonText: 'Email', extension: '.eml', filterType: FilterTypeEnum.Email },
  { id: 5, buttonText: 'All files', extension: '.txt', filterType: FilterTypeEnum.AllFiles },
  { id: 7, buttonText: 'Folder', type: FileIconType.folder, filterType: FilterTypeEnum.Folder },
];

const createImageProps = (src: string, alt: string) => ({
  imageProps: {
    src,
    alt,
    style: { width: '16px' },
  },
});

interface DropDownSearchProps {
  setShowGeneralSearchElement?: (show: boolean) => void;
  searchBoxStyles?: Partial<ISearchBoxStyles>;
  hideLocations?: boolean
}

interface FilterButtonProps {
  id?: number,
  extension?: string,
  type?: FileIconType,
  buttonText: string,
  handlerFunction?: (extension: number) => void,
  selectedFilter?: FilterTypeEnum | "",
  brand?: string,
  filterType: FilterTypeEnum
}

const FilterButton = ({ id, extension = "", type, buttonText, handlerFunction, selectedFilter, brand }: FilterButtonProps) => {
  const [pressed, setPressed] = React.useState(false);

  React.useEffect(() => {
    setPressed(selectedFilter === id);
  }, [selectedFilter]);

  const handleClick = () => {
    setPressed(!pressed);
    handlerFunction(id);
  };

  return (
    <div onMouseDown={(e) => { e.preventDefault(); handleClick() }} className={filterButtonStyles.filterButton} style={{ backgroundColor: '#faf9f8', cursor: "pointer" }}>
      <div className={filterButtonStyles.filterButtonIcon}>
        {extension !== ""
          ? <Icon
            {...getFileTypeIconProps({
              extension: extension,
              size: 16,
              imageFileType: "svg",
            })}
          />
          : brand !== undefined
            ? <img src={`https://res-1.cdn.office.net/files/fabric-cdn-prod_20230815.002/assets/brand-icons/product/svg/${brand}_20x1.svg`} height={16} width={16} />
            : type !== undefined
              ? <Icon
                {...getFileTypeIconProps({
                  type: type,
                  size: 16,
                  imageFileType: "svg",
                })}
              />
              : <Icon iconName="DocLibrary" className={dropDownSearchStyles.svgIcon} />
        }
      </div>
      <Text style={{ color: 'rgb(50, 49, 48)' }}>{buttonText}</Text>
    </div>
  )
}

const SearchMentionComponent = ({ mentionOptions, selectMention }) => (
  <Stack tokens={{ childrenGap: 3 }} className="mentionDropdown">
    {mentionOptions.map((option) => (
      <div key={option.name + option.email} onClick={() => selectMention(option)} className="mentionItem">
        <Persona
          text={option.name}
          secondaryText={option.email}
          size={PersonaSize.size32}
          styles={{ secondaryText: { display: 'block' } }}
        />
      </div>
    ))}
  </Stack>
)

// const getSearchHistory = (): string[] => {
//   let searchHistory: string[] = getLocalStorage(localStorageKeys.searchHistory);
//   if (!Array.isArray(searchHistory)) {
//     searchHistory = JSON.parse(StorageHelper.getItem(localStorageKeys.searchHistory));
//   }
//   return searchHistory ?? [];
// }

// const saveSearchHistory = (searchHistory: string[]): void => {
//   if (!setLocalStorage(localStorageKeys.searchHistory, searchHistory)) {
//     StorageHelper.setItemAsync(localStorageKeys.searchHistory, JSON.stringify(searchHistory));
//   }
// }

const DropDownSearch: React.FunctionComponent<DropDownSearchProps> = ({
  setShowGeneralSearchElement = () => { },
  searchBoxStyles,
  hideLocations = false
}: DropDownSearchProps) => {

  const searchBoxRef = React.useRef(null);
  const dropdownElementRef = React.useRef(null);

  const languageStrings = React.useContext(LanguageContext);

  const [searchPlaceholder, setSearchPlaceholder] = React.useState(languageStrings.Search);
  const [isDropdownVisible, setIsDropdownVisible] = React.useState<boolean>(false);

  const searchValueConentManagerFromRedux = useSelector((state: RootState) => state.contentManagerDirectory.searchValue);
  const workingDir = useSelector((state: RootState) => state.workingDirectories.workingDirectories);
  const currentDirectory = useSelector((state: RootState) => state.workingDirectories.workingDirectories.length > 0 ? state.workingDirectories.workingDirectories[state.workingDirectories.workingDirectories.length - 1] : null);
  const user = useSelector((state: RootState) => state.user);
  const mode = useSelector((state: RootState) => state.mode.mode);
  const enableAdvSearchMultiSites = useSelector((state: RootState) => state.tenantInfo?.enableAdvSearchMultiSites);

  const [hideLocationsInDropDown, setHideLocationsInDropDown] = React.useState<boolean>(hideLocations);

  const filterTypeFromRedux = useSelector((state: RootState) => state.search.searchValue.filterType);
  const filterTypeContentManagerFromRedux = useSelector((state: RootState) => state.contentManagerDirectory.searchValue.filterType);

  const [filterType, setFilterType] = React.useState<FilterTypeEnum | "">("");

  const [mentionOptions, setMentionOptions] = React.useState<{ name: string, email: string }[]>([]);
  const [allMentionOptions, setAllMentionOptions] = React.useState<{ name: string, email: string }[]>([]);
  const [isSearchMention, setIsSearchMention] = React.useState<boolean>(false);
  // const [searchHistory, setSearchHistory] = React.useState<string[]>(getSearchHistory());

  const [privateSearchValue, _setPrivateSearchValue] = React.useState<string>("");
  const privateSearchValueRef = React.useRef<string>("");
  const setPrivateSearchValue = (value: string) => {
    privateSearchValueRef.current = value;
    _setPrivateSearchValue(value);
  };

  const [advancedSearchError, setAdvancedSearchError] = React.useState<string>(undefined);
  const [isDialogHidden, setIsDialogHidden] = React.useState<boolean>(true);

  const showCallOutTimer = React.useRef<NodeJS.Timeout>();

  const [getAllUsers] = useLazyGetAllUsersQuery();
  const [getUsers] = useLazyGetUsersQuery();

  const dispatch = useDispatch();

  React.useEffect(() => {
    if (generalModule.isInConentMangerMode()) {
      dispatch(setContentManagerSearchFilterType(filterType))
    } else {
      dispatch(setSearchFilterType(filterType))
    }

    if (filterType !== "" && privateSearchValueRef.current?.trim()) {
      onSearchFilter(privateSearchValueRef.current);
    }
  }, [filterType]);

  React.useEffect(() => {
    setPrivateSearchValue("");
    setIsSearchMention(false);
    if (generalModule.isInConentMangerMode()) {
      dispatch(setContentManagerSearchValue(""));
      dispatch(setContentManagerSearchFilterType(""));
      setFilterType("");
    } else {
      dispatch(setSearch(""));
      dispatch(setSearchFilterType(""));
      setFilterType("");
    }

    if (!currentDirectory) {
      setSearchPlaceholder(languageStrings.Search);
    }
    else if (currentDirectory.displayName) {
      setSearchPlaceholder(`${languageStrings.Search} ${currentDirectory.displayName}`);
    }

    if (currentDirectory?.type !== undefined) {
      setHideLocationsInDropDown(true);
      return;
    }
    setHideLocationsInDropDown(false);
  }, [workingDir, currentDirectory]);

  React.useEffect(() => {
    if (generalModule.isInConentMangerMode() && searchValueConentManagerFromRedux.value === "" && privateSearchValueRef.current !== "") {
      setPrivateSearchValue("");
    }
  }, [searchValueConentManagerFromRedux]);

  React.useEffect(() => {
    if (generalModule.isInConentMangerMode() && filterTypeContentManagerFromRedux !== filterType && filterTypeContentManagerFromRedux === "") {
      setFilterType(filterTypeContentManagerFromRedux)
    } else if (!generalModule.isInConentMangerMode() && filterTypeFromRedux !== filterType && filterTypeFromRedux === "") {
      setFilterType(filterTypeFromRedux)
    }
  }, [filterTypeFromRedux, filterTypeContentManagerFromRedux]);

  React.useEffect(() => {
    (async () => {
      try {
        let getAllUsersResult = CacheHelper.get(DocubirdCache.users);
        if (!getAllUsersResult) {
          const request: IGraphGetUsersRequest = {
            orderby: 'displayName',
            select: 'id,displayName,mail,userPrincipalName'
          };
          getAllUsersResult = await getAllUsers({ request, accessToken: await authModule.getGraphAccessToken() }).unwrap();
          CacheHelper.set(DocubirdCache.users, getAllUsersResult);
        }

        if (getAllUsersResult?.value?.length > 0) {
          const options = getAllUsersResult?.value.map(user => {
            return {
              name: user.displayName,
              email: user.mail ?? (validateEmail(user.userPrincipalName) ? user.userPrincipalName : undefined)
            }
          })
          setMentionOptions(options);
          setAllMentionOptions(options);
        };

      } catch (error) {
        logger(error, "DropDownSearch.tsx (getUserProfilePicture, getAllUsers)");
      }
    })();
  }, []);

  const getMentionSearch = async (value: string) => {
    if (value?.startsWith("@")) {
      setIsSearchMention(true);
      if (value === "@") {
        setMentionOptions(allMentionOptions);
      } else {
        const request: IGraphGetUsersRequest = {
          filter: value.split('@')[1],
          orderby: 'displayName',
          select: 'id,displayName,mail,userPrincipalName'
        }

        try {
          const result = await getUsers({ request, accessToken: await authModule.getGraphAccessToken() }).unwrap();

          if (result) {
            const options = result?.value?.map(user => {
              return {
                name: user.displayName,
                email: user.mail ?? (validateEmail(user.userPrincipalName) ? user.userPrincipalName : undefined)
              }
            })
            setMentionOptions(options);
          }
        } catch (error) {
          logger(error, "DropDownSearch.tsx (getUsers)");
        }
      }
      setIsDropdownVisible(true);
      // dispatch(setOverlayVisibility(true));
    } else {
      setIsSearchMention(false);
    }
  };

  const selectMention = (mention) => {
    const mentionSearchValue: string = privateSearchValueRef.current?.trimStart().startsWith("@") ? privateSearchValueRef.current.split("@")[1] : "";
    const searchValue = privateSearchValueRef.current.replace(`@${mentionSearchValue}`, `@${mention.name}`);
    setPrivateSearchValue(searchValue);
    if (generalModule.isInConentMangerMode()) {
      dispatch(setContentManagerSearchValue(searchValue?.trim()));
    } else {
      onSearchFilter(searchValue);
    }
    setIsDropdownVisible(false);
    // dispatch(setOverlayVisibility(false));
  };

  // const saveSearchToHistory = (value: string) => {
  //   let tempArr = [...searchHistory];
  //   const index = tempArr.indexOf(value);
  //   if (index !== -1) {
  //     tempArr.splice(index, 1);
  //   }
  //   if (tempArr.length >= 4) {
  //     tempArr = tempArr.slice(0, 4);
  //   }
  //   tempArr.unshift(value);

  //   setSearchHistory(tempArr);
  //   saveSearchHistory(tempArr);
  // };

  const handleTextChange = (event?: React.ChangeEvent<HTMLInputElement>, value?: string) => {
    const searchValue: string = value?.trimStart();
    setPrivateSearchValue(searchValue);
    getMentionSearch(searchValue);
  }

  const onSearchFilter = async (value?: string) => {
    setShowGeneralSearchElement(true);
    setIsDropdownVisible(false);
    // dispatch(setOverlayVisibility(false));
    // saveSearchToHistory(value);
    if (generalModule.isInConentMangerMode()) {
      dispatch(setContentManagerSearchValue(value?.trim()));
    } else {
      dispatch(setSearch(value?.trim()));
    }
  }

  const onClearFilter = (e?: React.ChangeEvent<HTMLInputElement>,) => {
    clearTimeout(showCallOutTimer.current);
    setIsDropdownVisible(false);
    // dispatch(setOverlayVisibility(false));
    setIsSearchMention(false);
    setFilterType("");
    setPrivateSearchValue("");
    setShowGeneralSearchElement(false);
    if (generalModule.isInConentMangerMode()) {
      dispatch(setContentManagerSearchValue(""));
      dispatch(setContentManagerSearchFilterType(""));
    } else {
      dispatch(setSearch(""));
      dispatch(setSearchFilterType(""));
    }
    e.preventDefault();
  }

  const onEscapeFilter = () => {
  }

  const handleFilter = (filterTypeProp: FilterTypeEnum) => {
    if (filterType === filterTypeProp) setFilterType("");
    else {
      setFilterType(filterTypeProp);
      setIsDropdownVisible(false);
      // dispatch(setOverlayVisibility(false));
    }
  }

  // const handleDropdownBlur = () => {
  //   setIsDropdownVisible(false);
  //   // dispatch(setOverlayVisibility(false));
  // }

  // const handleDropdownFocus = () => {
  //   if (currentDirectory?.type === ComponentTypes.Exchange) {
  //     return;
  //   }

  //   showCallOutTimer.current = setTimeout(() => {
  //     setIsDropdownVisible(true);
  //     // dispatch(setOverlayVisibility(true));
  //   }, 75);
  // }

  // const handleHistorySearch = (value: string) => {
  //   setPrivateSearchValue(value);
  //   onSearchFilter(value);
  // }

  // const iconClickHandler = () => {
  //   if (currentDirectory?.type === ComponentTypes.Exchange) {
  //     if (privateSearchValueRef.current) {
  //       onSearchFilter(privateSearchValueRef.current);
  //     }
  //     return;
  //   }
  //   setIsDropdownVisible(true);
  //   // dispatch(setOverlayVisibility(true));
  // }

  // const getSearchBoxIcon = (): any => {
  //   switch (filterType) {
  //     case "":
  //       return { iconName: "Search" };
  //     case FilterTypeEnum.Library:
  //       return { iconName: "DocLibrary" };
  //     case FilterTypeEnum.Folder:
  //       return {
  //         ...getFileTypeIconProps({
  //           type: FileIconType.folder,
  //           size: 16,
  //           imageFileType: "svg",
  //         }), style: { height: '17px' }
  //       };
  //     case FilterTypeEnum.Teams:
  //       return createImageProps('https://res-1.cdn.office.net/files/fabric-cdn-prod_20230815.002/assets/brand-icons/product/svg/teams_16x1.svg', 'Teams Icon');
  //     case FilterTypeEnum.SharePoint:
  //       return createImageProps('https://res-1.cdn.office.net/files/fabric-cdn-prod_20230815.002/assets/brand-icons/product/svg/sharepoint_16x1.svg', 'SharePoint Icon');
  //     case FilterTypeEnum.OneDrive:
  //       return createImageProps('https://res-1.cdn.office.net/files/fabric-cdn-prod_20230815.002/assets/brand-icons/product/svg/onedrive_16x1.svg', 'OneDrive Icon');
  //     default:
  //       const icon = {
  //         ...getFileTypeIconProps({
  //           extension: searchingForArray[filterType].extension,
  //           size: 16,
  //           imageFileType: "svg",
  //         }), style: { height: '17px' }
  //       };
  //       return icon;
  //   }
  // };

  // React.useEffect(() => {
  //   const handleClickOutside = (event) => {
  //     if (dropdownElementRef.current && !dropdownElementRef.current.contains(event.target) && searchBoxRef.current && !searchBoxRef.current.contains(event.target)) {
  //       handleDropdownBlur();
  //     }
  //   };

  //   document.addEventListener("mousedown", handleClickOutside);

  //   return () => {
  //     document.removeEventListener("mousedown", handleClickOutside);
  //   };
  // }, [searchBoxRef]);

  const hideDialog = () => {
    setIsDialogHidden(true);
  }

  const showDialog = () => {
    setIsDialogHidden(false);
  }

  const openAdvancedSearchDialog = async () => {
    let advancedSearchDialog: Office.Dialog;
    const processEmailCreation = async (args: { message: string; origin: string; }) => {
      const result: any = JSON.parse(args.message);
      const emailer = (new EmailCreatorFactory(false)).create(languageStrings, mode);
      switch (result.type) {
        case EmailCreationTypeEnum.ShareableLinks:
          await emailer.withShareableLink(result.data);
          break;
        case EmailCreationTypeEnum.Attachments:
          await emailer.withAttachment(result.data);
          break;
      }
    };

    const context: IAdvancedSearchContext = {
      siteId: currentDirectory?.siteId ?? workingDir.slice().reverse().find(dir => dir.siteId)?.siteId,
      documentLibraryId: currentDirectory?.parentId ? currentDirectory.parentId : (currentDirectory?.sharePointListId ? currentDirectory?.id : ""),
      mode: mode,
      loadUserRecentItems: (user.settings?.showRecent ?? true) && (user.oneDriveWebUrl !== null || user.oneDriveWebUrl !== undefined),
      hasTeamsAccess: user.settings?.enableTeams ?? true,
      hasSharePointAccess: user.settings?.enableSharePoint ?? true,
      hubId: currentDirectory?.isHub && currentDirectory.hubId ? currentDirectory.hubId : undefined,
      teamGroupId: currentDirectory?.teamGroupId ?? undefined,
      teamDriveId: currentDirectory?.teamDriveId ?? undefined,
    };

    const account = await authModule.getAccount();
    const isTeams = !!(currentDirectory?.isTeamsFolder || currentDirectory?.isTeamsRootFolder);

    const url = new URL(process.env.baseUri + "/search.html");
    url.searchParams.append(shared.params.accountId, account.accountId);
    url.searchParams.append(shared.params.username, account.username);
    url.searchParams.append(shared.params.language, Office.context.displayLanguage);
    url.searchParams.append(advancedSearch.params.siteId, context.siteId);
    url.searchParams.append(advancedSearch.params.hubId, context.hubId);
    url.searchParams.append(advancedSearch.params.documentLibraryId, context.documentLibraryId);
    url.searchParams.append(advancedSearch.params.tenantId, account.tenantId);
    url.searchParams.append(advancedSearch.params.mode, context.mode.toString());
    url.searchParams.append(advancedSearch.params.loadUserRecentItems, context.loadUserRecentItems.toString());
    url.searchParams.append(advancedSearch.params.hasTeamsAccess, context.hasTeamsAccess.toString());
    url.searchParams.append(advancedSearch.params.hasSharePointAccess, context.hasSharePointAccess.toString());
    url.searchParams.append(advancedSearch.params.isTeams, isTeams.toString());
    url.searchParams.append(advancedSearch.params.allowOpenInSharePoint, user.tenantAllowOpenInSharePoint.toString());
    url.searchParams.append(advancedSearch.params.teamGroupId, context.teamGroupId);
    url.searchParams.append(advancedSearch.params.teamDriveId, context.teamDriveId);

    if (!generalModule.isInConentMangerMode()
      && generalModule.getIsOfficeJSAvailable()
      && Office.context.requirements.isSetSupported("Mailbox", "1.5")
      && Office.context.mailbox?.userProfile?.emailAddress) {
      url.searchParams.append(shared.params.mailboxAddress, Office.context.mailbox.userProfile.emailAddress);
    }

    Office.context.ui.displayDialogAsync(url.href, { height: 80, width: 80, promptBeforeOpen: false, displayInIframe: true }, result => {
      if (result.status === Office.AsyncResultStatus.Failed) {
        console.warn("Office.AsyncResultStatus.Failed: " + `${result.error.code} ${result.error.message}`);
        setAdvancedSearchError(result.error.message);
        showDialog();
        return;
      }

      advancedSearchDialog = result.value;
      advancedSearchDialog.addEventHandler(Office.EventType.DialogMessageReceived, processEmailCreation);
    });
  };

  const dialogContentProps = {
    type: DialogType.largeHeader,
    title: languageStrings.AdvancedSearch,
    closeButtonAriaLabel: "Close",
    subText: (advancedSearchError && advancedSearchError !== "") ? advancedSearchError : "Could not open the search window"
  };

  const advancedSearchErrorDialog = (
    <Dialog hidden={isDialogHidden} onDismiss={hideDialog} dialogContentProps={dialogContentProps}>
      <DialogFooter>
        <PrimaryButton autoFocus onClick={hideDialog} text="Ok" />
      </DialogFooter>
    </Dialog>
  );

  const onAdvancedSearchClick = () => {
    if (generalModule.isInConentMangerMode()) {
      dispatch(setMode(ModeEnum.contentManagerAdvancedSearch));
    }
    else {
      openAdvancedSearchDialog();
    }
  }

  const getIsAdvancedSearchButtonEnabled = () => {
    if (enableAdvSearchMultiSites
      && (generalModule.isInConentMangerMode() || !isMobile())
      && !workingDir.some(dir => dir?.type === ComponentTypes.OneDrive || dir?.type === ComponentTypes.OneDriveSubFolder)) {
      return true;
    }

    if (generalModule.isInConentMangerMode() || (!isMobile() && AdvancedSearchHelper.canView(currentDirectory?.type))) {
      return currentDirectory?.siteId?.length > 0
        || currentDirectory?.teamChannelId?.length > 0
        || workingDir.some(dir => dir?.siteId);
    }

    return false;
  }

  return (
    <>
      <Stack style={dropDownSearchStyles.stackMain}>
        <Stack.Item>
          <div ref={searchBoxRef}>
            <SearchBox
              autoComplete="off"
              styles={searchBoxStyles ? searchBoxStyles : localSearchBoxStyles}
              placeholder={searchPlaceholder}
              showIcon={true}
              value={privateSearchValue ?? ""}
              onChange={handleTextChange}
              onSearch={onSearchFilter}
              onClear={onClearFilter}
              onEscape={onEscapeFilter}
              // onFocus={handleDropdownFocus}
              // iconProps={getSearchBoxIcon()}
              // onClick={(e) => e.preventDefault()}
              // onIconClick={() => iconClickHandler()}
              onAdvancedSearchClick={onAdvancedSearchClick}
              showAdvancedSearchButton={getIsAdvancedSearchButtonEnabled()}
            />
          </div>
          {isDropdownVisible &&
            <Callout
              target={searchBoxRef.current}
              directionalHint={DirectionalHint.bottomLeftEdge}
              doNotLayer={false}
              isBeakVisible={false}
              gapSpace={5}
              setInitialFocus
              style={{
                minWidth: searchBoxStyles ? '25vw' : dropDownSearchStyles.callout.minWidth,
                maxWidth: searchBoxStyles ? '25vw' : dropDownSearchStyles.callout.minWidth,
              }}
              styles={{
                container: dropDownSearchStyles.calloutContainer,
                root: dropDownSearchStyles.calloutRoot,
              }}
            >
              {isSearchMention ? <SearchMentionComponent mentionOptions={mentionOptions} selectMention={selectMention} /> :
                <div id="dropdown-element" ref={dropdownElementRef}>
                  <div style={dropDownSearchStyles.dropdownElement}>
                    {!hideLocationsInDropDown && <div style={dropDownSearchStyles.dropdownSection}>
                      <Text styles={headingTextStyles}>{languageStrings.Location}</Text>
                      <div style={dropDownSearchStyles.dropdownSectionItems}>
                        {user.settings.enableTeams && <FilterButton buttonText="Teams" id={8} handlerFunction={() => { handleFilter(FilterTypeEnum.Teams); }} brand="teams" selectedFilter={filterType} filterType={FilterTypeEnum.Teams} />}
                        {user.settings.enableSharePoint && <FilterButton buttonText="SharePoint" id={9} handlerFunction={() => { handleFilter(FilterTypeEnum.SharePoint); }} brand="sharepoint" selectedFilter={filterType} filterType={FilterTypeEnum.SharePoint} />}
                        {user.settings.enableOneDrive && user.oneDriveWebUrl?.length > 0 && <FilterButton buttonText="OneDrive" id={10} handlerFunction={() => { handleFilter(FilterTypeEnum.OneDrive); }} brand="onedrive" selectedFilter={filterType} filterType={FilterTypeEnum.OneDrive} />}
                      </div>
                    </div>}
                    {/* {searchHistory.length > 0 &&
                    <div style={styles.dropdownSection}>
                      <Text>{languageStrings.History}</Text>
                      {searchHistory.map((item, index) => (
                        <div key={index} onClick={(e) => { e.preventDefault(); handleHistorySearch(item); }} style={styles.historySearchItem}>
                          <Icon iconName={"Recent"} style={styles.iconSpacing} />
                          <Text>{item}</Text>
                        </div>
                      ))}
                    </div>} */}
                    <div style={dropDownSearchStyles.dropdownSection}>
                      <Text styles={headingTextStyles}>{languageStrings.SearchingFor}</Text>
                      <div style={dropDownSearchStyles.dropdownSectionItems}>
                        {searchingForArray.map(item => !((item.id === 6 && (currentDirectory?.type !== ComponentTypes.SharePointDocuments && currentDirectory?.type !== ComponentTypes.GeneralSearch && currentDirectory !== null)) || (generalModule.isInConentMangerMode() && item.id === 7)) && (
                          <FilterButton
                            key={item.id}
                            id={item.id}
                            buttonText={item.buttonText}
                            extension={item.extension}
                            handlerFunction={() => handleFilter(item.filterType)}
                            selectedFilter={filterType}
                            type={item.type}
                            filterType={item.filterType}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>}
            </Callout>}
        </Stack.Item>
      </Stack>
      {advancedSearchErrorDialog}
    </>
  );
};

export default DropDownSearch;

